import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import apiV2 from "../../../services/apiV2";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import MasterModal from "../../../components/Master/MasterModal";
import MasterConfirmationModal from "../../../components/Master/MasterConfirmationModal";
import "../Plans/plans-page.css";

const CATEGORIES = [
	{ value: "avisos", label: "Servidor / Avisos" },
	{ value: "importante", label: "Importante" },
	{ value: "sistema_meta", label: "Sistema / Meta" },
	{ value: "atualizacoes", label: "Atualizações" },
	{ value: "eventos", label: "Eventos" },
];

const STATUSES = [
	{ value: "draft", label: "Rascunho" },
	{ value: "published", label: "Publicado" },
	{ value: "archived", label: "Arquivado" },
];

const emptyForm = () => ({
	title: "",
	body: "",
	category: "avisos",
	isPinned: false,
	visibility: "global",
	companyId: "",
	eventDate: "",
	status: "draft",
	metaErrorCode: "",
	metaExternalUrl: "",
	mentionUserIds: [],
	linkUrl: "",
	linkCaption: "",
});

function categoryLabel(value) {
	return CATEGORIES.find((c) => c.value === value)?.label || value;
}

function statusLabel(value) {
	return STATUSES.find((s) => s.value === value)?.label || value;
}

function toDateInput(value) {
	if (!value) return "";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "";
	return d.toISOString().slice(0, 10);
}

export default function MasterDocuments() {
	const { user } = useContext(AuthContext);
	const [loading, setLoading] = useState(true);
	const [docs, setDocs] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [companyUsers, setCompanyUsers] = useState([]);
	const [filters, setFilters] = useState({
		category: "",
		status: "",
		q: "",
		dateFrom: "",
		dateTo: "",
	});
	const [qInput, setQInput] = useState("");
	const [count, setCount] = useState(0);
	const [stats, setStats] = useState({
		total: 0,
		published: 0,
		draft: 0,
		countsByCategory: {},
	});
	const [modalOpen, setModalOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm());
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [uploading, setUploading] = useState(false);

	const routeMeta = getMasterRouteMeta("/admin-master/documents");

	const loadCompanies = useCallback(async () => {
		try {
			const { data } = await api.get("/master/companies");
			const list = Array.isArray(data?.companies)
				? data.companies
				: Array.isArray(data)
					? data
					: [];
			setCompanies(list);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadDocs = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await apiV2.get("/master/documents", {
				params: {
					category: filters.category || undefined,
					status: filters.status || undefined,
					q: filters.q || undefined,
					dateFrom: filters.dateFrom || undefined,
					dateTo: filters.dateTo || undefined,
					limit: 100,
				},
			});
			setDocs(Array.isArray(data?.documents) ? data.documents : []);
			setCount(Number(data?.count) || 0);
			if (data?.stats) {
				setStats({
					total: Number(data.stats.total) || 0,
					published: Number(data.stats.published) || 0,
					draft: Number(data.stats.draft) || 0,
					countsByCategory: data.stats.countsByCategory || {},
				});
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [filters.category, filters.status, filters.q, filters.dateFrom, filters.dateTo]);

	useEffect(() => {
		const t = setTimeout(() => {
			setFilters((f) => ({ ...f, q: qInput.trim() }));
		}, 300);
		return () => clearTimeout(t);
	}, [qInput]);

	useEffect(() => {
		if (user?.profile !== ROLES.master) return;
		loadCompanies();
	}, [user, loadCompanies]);

	useEffect(() => {
		if (user?.profile !== ROLES.master) return;
		loadDocs();
	}, [user, loadDocs]);

	useEffect(() => {
		const loadUsers = async () => {
			if (form.visibility !== "company" || !form.companyId) {
				setCompanyUsers([]);
				return;
			}
			try {
				const { data } = await api.get("/master/companies/" + form.companyId);
				const users = Array.isArray(data?.users)
					? data.users
					: Array.isArray(data?.company?.users)
						? data.company.users
						: [];
				setCompanyUsers(users);
			} catch {
				setCompanyUsers([]);
			}
		};
		loadUsers();
	}, [form.visibility, form.companyId]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm());
		setModalOpen(true);
	};

	const openEdit = (doc) => {
		setEditing(doc);
		setForm({
			title: doc.title || "",
			body: doc.body || "",
			category: doc.category || "avisos",
			isPinned: !!doc.isPinned,
			visibility: doc.visibility || "global",
			companyId: doc.companyId ? String(doc.companyId) : "",
			eventDate: toDateInput(doc.eventDate),
			status: doc.status || "draft",
			metaErrorCode: doc.metaErrorCode || "",
			metaExternalUrl: doc.metaExternalUrl || "",
			mentionUserIds: (doc.mentions || []).map((m) => m.userId),
			linkUrl: "",
			linkCaption: "",
		});
		setModalOpen(true);
	};

	const buildPayload = () => {
		const payload = {
			title: form.title.trim(),
			body: form.body,
			category: editing?.id ? form.category : "avisos",
			isPinned: !!form.isPinned,
			visibility: form.visibility,
			status: form.status,
			metaErrorCode: form.metaErrorCode.trim() || null,
			metaExternalUrl: form.metaExternalUrl.trim() || null,
			eventDate: form.eventDate || null,
			mentionUserIds: form.mentionUserIds,
			companyId:
				form.visibility === "company" && form.companyId
					? Number(form.companyId)
					: null,
		};
		return payload;
	};

	const handleSave = async () => {
		if (!form.title.trim()) {
			toast.error("Título obrigatório");
			return;
		}
		if (form.visibility === "company" && !form.companyId) {
			toast.error("Selecione a empresa");
			return;
		}
		setSaving(true);
		try {
			const payload = buildPayload();
			if (editing?.id) {
				await apiV2.put(`/master/documents/${editing.id}`, payload);
				toast.success("Informativo atualizado");
			} else {
				await apiV2.post("/master/documents", payload);
				toast.success("Informativo criado");
			}
			setModalOpen(false);
			loadDocs();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget?.id) return;
		try {
			await apiV2.delete(`/master/documents/${deleteTarget.id}`);
			toast.success("Informativo removido");
			setDeleteTarget(null);
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleAddLink = async () => {
		if (!editing?.id || !form.linkUrl.trim()) return;
		try {
			await apiV2.post(`/master/documents/${editing.id}/attachments`, {
				type: "link",
				url: form.linkUrl.trim(),
				caption: form.linkCaption.trim(),
			});
			toast.success("Link adicionado");
			setForm((f) => ({ ...f, linkUrl: "", linkCaption: "" }));
			const { data } = await apiV2.get(`/master/documents/${editing.id}`);
			setEditing(data);
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleUploadImage = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file || !editing?.id) return;
		setUploading(true);
		try {
			const fd = new FormData();
			fd.append("file", file);
			await apiV2.post(`/master/documents/${editing.id}/attachments`, fd);
			toast.success("Imagem enviada");
			const { data } = await apiV2.get(`/master/documents/${editing.id}`);
			setEditing(data);
			loadDocs();
		} catch (err) {
			toastError(err);
		} finally {
			setUploading(false);
		}
	};

	const handleRemoveAttachment = async (attId) => {
		if (!editing?.id) return;
		try {
			await apiV2.delete(`/master/documents/${editing.id}/attachments/${attId}`);
			const { data } = await apiV2.get(`/master/documents/${editing.id}`);
			setEditing(data);
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const toggleMention = (userId) => {
		setForm((f) => {
			const has = f.mentionUserIds.includes(userId);
			return {
				...f,
				mentionUserIds: has
					? f.mentionUserIds.filter((id) => id !== userId)
					: [...f.mentionUserIds, userId],
			};
		});
	};

	const filteredHint = useMemo(() => {
		if (loading) return "Carregando...";
		if (!count) return "Nenhum informativo encontrado.";
		return `${count} informativo${count === 1 ? "" : "s"} no filtro`;
	}, [count, loading]);

	const clearFilters = () => {
		setQInput("");
		setFilters({ category: "", status: "", q: "", dateFrom: "", dateTo: "" });
	};

	if (user?.profile !== ROLES.master) {
		return null;
	}

	return (
		<div className="mp-page master-plans-page">
			<MasterPageBreadcrumb meta={routeMeta} />

			<div className="master-plans-page__stats">
				<div className="master-plans-page__stat">
					<span className="master-plans-page__stat-dot" style={{ background: "#6366f1" }} />
					<span className="master-plans-page__stat-label">Total</span>
					<span className="master-plans-page__stat-value">{stats.total}</span>
				</div>
				<div className="master-plans-page__stat">
					<span className="master-plans-page__stat-dot" style={{ background: "#10b981" }} />
					<span className="master-plans-page__stat-label">Publicados</span>
					<span className="master-plans-page__stat-value">{stats.published}</span>
				</div>
				<div className="master-plans-page__stat">
					<span className="master-plans-page__stat-dot" style={{ background: "#f59e0b" }} />
					<span className="master-plans-page__stat-label">Rascunhos</span>
					<span className="master-plans-page__stat-value">{stats.draft}</span>
				</div>
				<div className="master-plans-page__stat">
					<span className="master-plans-page__stat-dot" style={{ background: "#3b82f6" }} />
					<span className="master-plans-page__stat-label">No filtro</span>
					<span className="master-plans-page__stat-value">{count}</span>
				</div>
			</div>

			<div
				className="mp-toolbar"
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: 10,
					alignItems: "flex-end",
					justifyContent: "space-between",
					marginBottom: 16,
				}}
			>
				<div
					className="mp-toolbar__filters"
					style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", flex: 1 }}
				>
					<label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#71717a" }}>
						Busca
						<input
							className="form-input form-input-sm"
							placeholder="Título, conteúdo, erro Meta..."
							value={qInput}
							onChange={(e) => setQInput(e.target.value)}
							style={{ minWidth: 220 }}
						/>
					</label>
					<label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#71717a" }}>
						Categoria
						<select
							className="form-select form-select-sm"
							value={filters.category}
							onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
						>
							<option value="">Todas</option>
							{CATEGORIES.map((c) => (
								<option key={c.value} value={c.value}>
									{c.label} ({Number(stats.countsByCategory?.[c.value] || 0)})
								</option>
							))}
						</select>
					</label>
					<label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#71717a" }}>
						Status
						<select
							className="form-select form-select-sm"
							value={filters.status}
							onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
						>
							<option value="">Todos</option>
							{STATUSES.map((s) => (
								<option key={s.value} value={s.value}>
									{s.label}
								</option>
							))}
						</select>
					</label>
					<label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#71717a" }}>
						De
						<input
							type="date"
							className="form-input form-input-sm"
							value={filters.dateFrom}
							onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
						/>
					</label>
					<label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#71717a" }}>
						Até
						<input
							type="date"
							className="form-input form-input-sm"
							value={filters.dateTo}
							onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
						/>
					</label>
					{(qInput || filters.category || filters.status || filters.dateFrom || filters.dateTo) && (
						<button type="button" className="btn btn-sm" onClick={clearFilters}>
							Limpar
						</button>
					)}
				</div>
				<button type="button" className="btn btn-sm bg-primary text-white" onClick={openCreate}>
					Novo informativo
				</button>
			</div>

			<p className="text-sm text-default-500 mb-3">{filteredHint}</p>

			{loading ? (
				<MasterSpinner />
			) : (
				<div className="mp-grid">
					{docs.map((doc) => (
						<article key={doc.id} className="mp-card">
							<div className="mp-card__header">
								<div className="min-w-0">
									<h3 className="mp-card__name truncate">{doc.title}</h3>
									<div className="mp-card__meta">
										<span>{categoryLabel(doc.category)}</span>
										<span className="mp-card__meta-sep">·</span>
										<span>{statusLabel(doc.status)}</span>
										<span className="mp-card__meta-sep">·</span>
										<span>
											{doc.visibility === "global"
												? "Todas empresas"
												: doc.companyName || `Empresa #${doc.companyId}`}
										</span>
									</div>
								</div>
								{doc.isPinned ? (
									<span className="mp-card__badge mp-card__badge--active">Fixado</span>
								) : null}
							</div>
							<div className="mp-card__actions" style={{ display: "flex", gap: 8 }}>
								<button type="button" className="btn btn-sm" onClick={() => openEdit(doc)}>
									Editar
								</button>
								<button
									type="button"
									className="btn btn-sm"
									onClick={() => setDeleteTarget(doc)}
								>
									Excluir
								</button>
							</div>
						</article>
					))}
				</div>
			)}

			<MasterModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title={editing ? "Editar informativo" : "Novo informativo"}
				maxWidth={720}
			>
				<div className="space-y-3" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<label className="block text-sm">
						Título
						<input
							className="form-input form-input-sm mt-1 w-full"
							value={form.title}
							onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
						/>
					</label>
					<label className="block text-sm">
						Conteúdo
						<textarea
							className="form-textarea form-textarea-sm mt-1 w-full"
							rows={6}
							value={form.body}
							onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
						/>
					</label>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<label className="block text-sm">
							Categoria
							<input
								className="form-input form-input-sm mt-1 w-full"
								value={categoryLabel(form.category || "avisos")}
								disabled
								readOnly
							/>
							<span className="text-xs text-default-500">
								Avisos do servidor usam a categoria fixa.
							</span>
						</label>
						<label className="block text-sm">
							Status
							<select
								className="form-select form-select-sm mt-1 w-full"
								value={form.status}
								onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
							>
								{STATUSES.map((s) => (
									<option key={s.value} value={s.value}>
										{s.label}
									</option>
								))}
							</select>
						</label>
						<label className="block text-sm">
							Escopo
							<select
								className="form-select form-select-sm mt-1 w-full"
								value={form.visibility}
								onChange={(e) =>
									setForm((f) => ({
										...f,
										visibility: e.target.value,
										companyId: e.target.value === "global" ? "" : f.companyId,
									}))
								}
							>
								<option value="global">Todas as empresas</option>
								<option value="company">Empresa específica</option>
							</select>
						</label>
						{form.visibility === "company" ? (
							<label className="block text-sm">
								Empresa
								<select
									className="form-select form-select-sm mt-1 w-full"
									value={form.companyId}
									onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value }))}
								>
									<option value="">Selecione...</option>
									{companies.map((c) => (
										<option key={c.id} value={c.id}>
											{c.name}
										</option>
									))}
								</select>
							</label>
						) : (
							<div />
						)}
						<label className="block text-sm">
							Data (opcional)
							<input
								type="date"
								className="form-input form-input-sm mt-1 w-full"
								value={form.eventDate}
								onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))}
							/>
						</label>
						<label className="block text-sm" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24 }}>
							<input
								type="checkbox"
								checked={form.isPinned}
								onChange={(e) => setForm((f) => ({ ...f, isPinned: e.target.checked }))}
							/>
							Fixar (Importante)
						</label>
					</div>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<label className="block text-sm">
							Código erro Meta
							<input
								className="form-input form-input-sm mt-1 w-full"
								value={form.metaErrorCode}
								onChange={(e) => setForm((f) => ({ ...f, metaErrorCode: e.target.value }))}
							/>
						</label>
						<label className="block text-sm">
							URL externa Meta
							<input
								className="form-input form-input-sm mt-1 w-full"
								value={form.metaExternalUrl}
								onChange={(e) => setForm((f) => ({ ...f, metaExternalUrl: e.target.value }))}
							/>
						</label>
					</div>

					{form.visibility === "company" && companyUsers.length > 0 ? (
						<div>
							<p className="text-sm font-medium mb-2">Marcar usuários</p>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 120, overflow: "auto" }}>
								{companyUsers.map((u) => (
									<label key={u.id} className="text-xs" style={{ display: "flex", gap: 4, alignItems: "center" }}>
										<input
											type="checkbox"
											checked={form.mentionUserIds.includes(u.id)}
											onChange={() => toggleMention(u.id)}
										/>
										{u.name}
									</label>
								))}
							</div>
						</div>
					) : null}

					{editing?.id ? (
						<div style={{ borderTop: "1px solid var(--app-border, #e4e4e7)", paddingTop: 12 }}>
							<p className="text-sm font-medium mb-2">Anexos</p>
							{(editing.attachments || []).map((att) => (
								<div key={att.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
									<a href={att.url} target="_blank" rel="noreferrer" className="text-sm truncate">
										[{att.type}] {att.caption || att.url}
									</a>
									<button type="button" className="btn btn-xs" onClick={() => handleRemoveAttachment(att.id)}>
										Remover
									</button>
								</div>
							))}
							<div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
								<input
									className="form-input form-input-sm"
									placeholder="URL do link"
									value={form.linkUrl}
									onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
								/>
								<input
									className="form-input form-input-sm"
									placeholder="Legenda"
									value={form.linkCaption}
									onChange={(e) => setForm((f) => ({ ...f, linkCaption: e.target.value }))}
								/>
								<button type="button" className="btn btn-sm" onClick={handleAddLink}>
									Add link
								</button>
								<label className="btn btn-sm" style={{ cursor: "pointer" }}>
									{uploading ? "Enviando..." : "Upload imagem"}
									<input type="file" accept="image/*" hidden onChange={handleUploadImage} disabled={uploading} />
								</label>
							</div>
						</div>
					) : (
						<p className="text-xs text-default-500">Salve o informativo para adicionar anexos.</p>
					)}

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
						<button type="button" className="btn btn-sm" onClick={() => setModalOpen(false)}>
							Cancelar
						</button>
						<button
							type="button"
							className="btn btn-sm bg-primary text-white"
							disabled={saving}
							onClick={handleSave}
						>
							{saving ? "Salvando..." : "Salvar"}
						</button>
					</div>
				</div>
			</MasterModal>

			<MasterConfirmationModal
				open={!!deleteTarget}
				title="Excluir informativo"
				onConfirm={handleDelete}
				onClose={() => setDeleteTarget(null)}
			>
				{`Remover "${deleteTarget?.title || ""}"?`}
			</MasterConfirmationModal>
		</div>
	);
}
